<template>
  <div>
    <video-modal :show="showPlayer" @hide="stop" class="video-modal">
      <div slot="loading" class="loading-container vjs-waiting" v-if="!playerReady">
        <div class="vjs-loading-spinner" dir="ltr">
        </div>
      </div>
      <div slot="body" class="videojs-player" @click.stop v-show="playerReady">
        <video :id="playerElement" class="video-js vjs-big-play-centered">
          <source v-if="record.url"
            :src="record.url"
            :type="record.mimetype"
          >
          </source>
        </video>
      </div>
    </video-modal>
  </div>
</template>

<script>
// Record player component
// Reponsible for intitializing a videojs instance, loading and playing videos.
// This component was designed to be a singleton: when a video is to be played
// (triggered by a global 'play-record' event), it is loaded into the
// existing videojs instance (stopping any current playback).

import videojs from 'video.js'
require('video.js/dist/video-js.css')
import Thumbnails from '../../../libs/videojs-thumbnails/videojs.thumbnails.js'
require('../../../libs/videojs-thumbnails/videojs.thumbnails.css')

videojs.registerPlugin('thumbnails', Thumbnails)

import VideoModal from './VideoModal.vue'
import events from '../../../services/events'
import debounce from 'lodash.debounce'

export default {
  components: {
    VideoModal
  },
  data () {
    return {
      showPlayer: false,
      playerReady: false,
      record: {
        url: null
      },
      playerElement: `videojs-${this._uid}`,
      playerOptions: {
        'controls': true,
        'autoplay': false,
        'preload': false
      }
    }
  },
  watch: {
    'showPlayer': function (newValue, oldValue) {
      if (!newValue && oldValue) {
        this.player.pause()
      }
    }
  },
  mounted: function () {
    this.resizeHandler = debounce(this.adjustRatioEvent, 300)
    window.addEventListener('resize', this.resizeHandler)
    events.bus.$on('play-record', this.play)
    this.setupPlayer()
  },
  methods: {
    play (record) {
      this.record = record
      if (this.record.height && this.record.width) {
        this.adjustRatio(this.record.width / this.record.height)
      }
      this.playerReady = false
      this.showPlayer = true
      this.$nextTick(function () {
        this.player.on('loadedmetadata', () => {
          this.adjustRatio()
          this.playerReady = true
        })
        this.player.pause()
        this.player.loadingSpinner.show()
        this.player.src(record.url)
        this.player.poster(record.thumbnail)
        if (record.sprite) {
          this.setUpThumbnailPreviews(this.player, record.sprite)
        } else {
          this.clearThumbnailPreviews()
        }
        this.player.load()
        this.player.play()
      })
    },
    stop () {
      this.showPlayer = false
      this.player.pause()
    },
    setupPlayer: function () {
      if (this.playerSetUp) {
        return
      }
      var _this = this
      videojs(this.playerElement, this.playerOptions, function () {
        _this.player = this
        _this.playerSetUp = true
        _this.player.thumbnails()
      })
    },
    clearThumbnailPreviews () {
      this.player.thumbnails().updateState({})
    },
    // generate the required css for the thumbnail previews
    // when hovering in the video control bar
    setUpThumbnailPreviews (player, sprite) {
      let frames = Math.floor(sprite.width / sprite.h_interval)
      let options = {
        0: {
          src: sprite.url,
          style: {
            height: sprite.height + 'px',
            width: sprite.width + 'px',
            clip: `rect(0, ${sprite.h_interval}px, ${sprite.height}px, 0)`,  // (top, right, bottom, left)
            left: `-${sprite.h_interval / 2}px`
          }
        }
      }
      for (var i = 1; i < frames; i++) {
        options[i] = {
          style: {
            clip: `rect(0, ${sprite.h_interval * (i + 1)}px, ${sprite.height}px, ${sprite.h_interval * i}px)`,
            left: `-${(sprite.h_interval / 2) + sprite.h_interval * i}px`
          }
        }
      }
      player.thumbnails().updateState(options)
    },
    adjustRatioEvent (event) {
      this.adjustRatio()
    },
    // manually adjust the video size to make it fit in the screen,
    // taking into account the aspect ratio.
    // (there might be ways to achieve this with pure CSS)
    adjustRatio (ratio) {
      // accept an optional ratio argument, otherwise use the video's ratio
      if (this.playerSetUp) {
        if (!ratio) {
          ratio = this.player.videoWidth() / this.player.videoHeight()
        }
        var el = document.getElementById(this.playerElement)
        if (ratio && el) {
          let pageHeight = window.innerHeight ||
          document.documentElement.clientHeight ||
          document.body.clientHeight
          let pageWidth = window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth
          let maxHeight = pageHeight * 0.8
          let maxWidth = pageWidth * 0.8
          if (ratio >= 1) { // horizontal video
            // set width to 80% of window size
            el.style.width = maxWidth + 'px'
            el.style.height = Math.floor(maxWidth / ratio) + 'px'
          } else { // vertical video
            // set height to 80% of window size
            el.style.width = Math.floor(maxHeight * ratio) + 'px'
            el.style.height = maxHeight + 'px'
          }
        }
      }
    }
  },
  beforeDestroy () {
    this.player.dispose()
    window.removeEventListener('resize', this.resizeHandler)
  }
}
</script>



// WEBPACK FOOTER //
// ModalRecordPlayer.vue?36792a96